// 등급 기준 상세 가이드 공통 모달 컴포넌트
// AppBarSection, PriceQuoteGradeDisplay, EnhancedGradeSelection에서 공통 사용

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide,
  Tabs,
  Tab,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Smartphone as SmartphoneIcon,
  Headphones as HeadphonesIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DeviceGrade } from '../../../types/gradeSystem';
import { getGradeColor, getGradeDescription } from '../../../utils/gradePriceUtils';
import { getGradesForCategory } from '../../../constants/grades';

// 등급별 색상 정보
export const GRADE_INFO: Record<string, { label: string; description: string; color: string }> = {
  A: { label: 'A등급', description: '기능 정상, 외관 흠집 없음', color: '#22c55e' },
  B: { label: 'B등급', description: '기능 정상, 외관 흠집/잔상 있음', color: '#3b82f6' },
  C: { label: 'C등급', description: '기능 정상, 외관 파손 있음', color: '#f59e0b' },
  D: { label: 'D등급', description: 'LCD/기능 불량 또는 심한 파손', color: '#ef4444' },
  E: { label: 'E등급', description: '전원 불가, 침수, 부품용', color: '#6b7280' },
};

// 스마트폰 등급별 상세 정보 및 체크리스트
export const GRADE_DETAILS: Record<string, { label: string; description: string; checklist: string[] }> = {
  A: {
    label: 'A등급',
    description: '기능 정상 작동 및 외관 흠집이 없는 상태',
    checklist: [
      '액정: 깨끗하고 스크래치 없음',
      '후면: 흠집이나 손상 없음',
      '잔상: 잔상 없음',
      'LCD : 정상 작동',
      '전원: 정상 작동',
      '카메라: 모든 기능 정상',
      'Face ID: 정상 작동',
      '나침반: 정상 작동',
      '음성녹음: 정상 작동'
    ],
  },
  B: {
    label: 'B등급',
    description: '기능 문제없고 외관 흠집, 액정 흠집/잔상이 있는 상태 (생활기스, 잔기스 등)',
    checklist: [
      '액정: 흠집',
      '후면: 흠집',
      '잔상: 약진상 ~ 중진상',
      'LCD : 정상 작동',
      '전원: 정상 작동',
      '카메라: 모든 기능 정상',
      'Face ID: 정상 작동',
      '나침반: 정상 작동',
      '음성녹음: 정상 작동'
    ],
  },
  C: {
    label: 'C등급',
    description: '기능 문제없고, 외관 파손이 있는 상태(깨짐, 뜸, 심한 흠집 등)',
    checklist: [
      '액정: 심한 흠집, 파손, 전면 뜸',
      '후면: 심한 흠집, 파손, 후면 뜸',
      '잔상: 강진상, 백화 1~2개',
      'LCD : 정상 작동',
      '전원: 정상 작동',
      '카메라: 불량, 카메라 유리파손',
      'Face ID: 정상 작동',
      '나침반: 불량',
      '음성녹음: 불량'
    ],
  },
  D: {
    label: 'D등급',
    description: 'LCD, 기능 불량 또는 외관 파손이 있는 상태 (심한 깨짐, 몸체 휨 등)',
    checklist: [
      '액정: 심각한 파손',
      '후면: 몸체 휨',
      '잔상: 심한 잔상, 백화 3개이상, LCD 불량',
      'LCD : 불량, 멍, 얼룩, 줄감',
      '전원: 정상 작동',
      '카메라: 불량 ',
      '지문/Face ID: 홈버튼 불량, FACE ID 불량',
      '나침반: 불량',
      '음성녹음: 불량'
    ],
  },
  E: {
    label: 'E등급',
    description: '전원 불가, 침수, 부품용 등 정상 사용이 불가능한 상태',
    checklist: [
      '액정: 심각한 파손 또는 없음',
      '후면: 심각한 손상, 몸체 휨/파손',
      '잔상: 확인 불가',
      'LCD: 확인 불가 또는 불량',
      '전원: 전원 불가 또는 불안정',
      '카메라: 불량 또는 확인 불가',
      'Face ID: 불량 또는 확인 불가',
      '침수: 침수 이력 있음',
      '기타: 부품용으로만 활용 가능'
    ],
  },
};

// 웨어러블 등급별 상세 정보 및 체크리스트
const WEARABLE_GRADE_DETAILS: Record<string, { label: string; description: string; checklist: string[] }> = {
  A: {
    label: 'A등급',
    description: '미세한 흠집 0~3개 이내',
    checklist: [
      '외관: 미세한 흠집 0~3개 이내',
      '기능: 모든 기능 정상 작동',
      '충전: 유닛 및 케이스 정상 충전',
      '블루투스: 페어링 및 연결 정상',
      '음질: 좌/우 유닛 모두 정상 출력'
    ],
  },
  B: {
    label: 'B등급',
    description: '작은 흠집 0~3개 이내',
    checklist: [
      '외관: 작은 흠집 0~3개 이내',
      '기능: 모든 기능 정상 작동',
      '충전: 유닛 및 케이스 정상 충전',
      '블루투스: 페어링 및 연결 정상',
      '음질: 좌/우 유닛 모두 정상 출력'
    ],
  },
  C: {
    label: 'C등급',
    description: '눈에 보이는 큰 흠집이 있거나 흠집이 다수 존재하는 제품 (최대 5개 이내)',
    checklist: [
      '외관: 눈에 보이는 큰 흠집 존재',
      '외관: 또는 흠집이 다수 존재 (최대 5개 이내)',
      '기능: 기본 기능 정상 작동',
      '충전: 정상 충전 가능',
      '블루투스: 연결 가능'
    ],
  },
  D: {
    label: 'D등급',
    description: '완전고장, 파손',
    checklist: [
      '외관: 심한 파손, 깨짐, 외관의 흠집(크랙)',
      '스피커: 잡음, 노이즈 등 고장',
      '마이크: 고장',
      '충전: 충전 불량, 짐셋 고장',
      '기능: 완전고장 또는 주요 기능 불량'
    ],
  },
};

// 웨어러블 차감 기준 데이터
const WEARABLE_DEDUCTION_TABLE = [
  { model: '에어팟 1세대', cost: '유닛 1대당', criteria: '마이크 고장, 외관의 흠집(크랙) 등' },
  { model: '에어팟 2세대', cost: '유닛 1대당', criteria: '스피커(잡음, 노이즈 등)고장, 마이크 고장' },
  { model: '에어팟 3세대', cost: '유닛 1대당', criteria: '스피커(잡음, 노이즈 등)고장' },
  { model: '에어팟 Pro 1세대', cost: '유닛 1대당', criteria: '스피커(잡음, 노이즈 등)고장, 마이크 고장, 짐셋 고장, 외관의 흠집(크랙) 등' },
];

interface GradeCriteriaGuideDialogProps {
  open: boolean;
  onClose: () => void;
  deviceType?: string;
  selectedGrade?: DeviceGrade | null;
}

const GradeCriteriaGuideDialog: React.FC<GradeCriteriaGuideDialogProps> = ({
  open,
  onClose,
  deviceType,
  selectedGrade,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveTab(deviceType === '웨어러블' || deviceType === 'wearable' ? 1 : 0);
    }
  }, [open, deviceType]);

  const isWearableTab = activeTab === 1;
  const currentGradeDetails = isWearableTab ? WEARABLE_GRADE_DETAILS : GRADE_DETAILS;
  const grades = getGradesForCategory(isWearableTab ? '웨어러블' : '스마트폰') as DeviceGrade[];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' } as any}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          overflow: 'hidden',
        }
      }}
    >
      {/* 헤더 */}
      <DialogTitle
        sx={{
          p: 0,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              등급 기준 상세 가이드
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            기기의 상태에 따라 등급이 결정되며, 각 등급별 상세 기준을 확인하세요.
          </Typography>
        </Box>

        {/* 탭 */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 3,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: 'white',
            },
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 600,
              minHeight: 48,
              '&.Mui-selected': { color: 'white' },
            },
          }}
        >
          <Tab icon={<SmartphoneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="스마트폰" />
          <Tab icon={<HeadphonesIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="웨어러블" />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 2, bgcolor: '#f8fafc' }}>
        {/* 등급 카드 그리드 */}
        <Grid container spacing={2}>
          {grades.map((grade) => {
            const isSelected = selectedGrade === grade;
            const color = GRADE_INFO[grade].color;
            return (
              <Grid item xs={12} sm={6} key={grade}>
                <Card
                  elevation={0}
                  sx={{
                    border: isSelected ? `2px solid ${color}` : '1px solid #e2e8f0',
                    borderRadius: 3,
                    height: '100%',
                    bgcolor: 'white',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: `${color}80`, boxShadow: `0 4px 12px ${color}15` },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* 등급 헤더 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: 2,
                        background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 800, fontSize: '0.9rem',
                        boxShadow: `0 2px 8px ${color}40`,
                      }}>
                        {grade}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                          {currentGradeDetails[grade].label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.3 }}>
                          {currentGradeDetails[grade].description}
                        </Typography>
                      </Box>
                      {grade === 'E' && !isWearableTab && (
                        <Chip label="스마트폰 전용" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#64748b' }} />
                      )}
                    </Box>

                    {/* 체크리스트 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {currentGradeDetails[grade].checklist.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 14, color: `${color}90`, mt: '3px', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* 하단 정보 섹션 */}
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 매입불가 안내 (웨어러블) */}
          {isWearableTab && (
            <Card elevation={0} sx={{ border: '1px solid #fecaca', borderRadius: 3, bgcolor: '#fef2f2' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BlockIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                    매입불가
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#991b1b', fontSize: '0.8rem' }}>
                  Find my 미제거, 비정품은 매입이 불가합니다.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* 차감 기준표 (웨어러블) */}
          {isWearableTab && (
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: 'white' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    차감 기준
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    아래 항목에 해당하는 경우 유닛 1대당 금액이 차감됩니다.
                  </Typography>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>모델</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>차감 단위</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>차감 사유</th>
                      </tr>
                    </thead>
                    <tbody>
                      {WEARABLE_DEDUCTION_TABLE.map((row, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? '#f8fafc' : 'white' }}>
                          <td style={{ padding: '10px 16px', fontSize: '0.8rem', fontWeight: 600, color: '#334155', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{row.model}</td>
                          <td style={{ padding: '10px 16px', fontSize: '0.75rem', color: '#64748b', textAlign: 'center', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{row.cost}</td>
                          <td style={{ padding: '10px 16px', fontSize: '0.8rem', color: '#475569', borderBottom: '1px solid #f1f5f9', lineHeight: 1.5 }}>{row.criteria}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* 주의사항 */}
          <Card elevation={0} sx={{ border: '1px solid #fde68a', borderRadius: 3, bgcolor: '#fffbeb' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ fontSize: 18, color: '#d97706' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                  주의사항
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {isWearableTab ? (
                  <>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      실제 검수 결과에 따라 <strong>최종 등급과 금액</strong>이 달라질 수 있습니다.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      구성(<strong>풀세트/유닛/본체</strong>)에 따라 매입 가격이 다릅니다.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      실제 검수 결과에 따라 <strong>최종 등급과 금액</strong>이 달라질 수 있습니다.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      등급 내에서도 <strong>잔상/기능 불량</strong> 등에 따라 금액 차감이 발생할 수 있습니다.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      기능이 정상이어도 <strong>LCD 불량</strong>은 <strong>D등급</strong>으로 판정됩니다.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', pl: 0.5 }}>
                      <strong>아이폰</strong>의 경우 <strong>배터리 효율</strong>에 따라 금액 차감이 발생할 수 있습니다.
                    </Typography>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            py: 1.3,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '0.9rem',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            boxShadow: '0 4px 12px rgba(30,41,59,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            },
          }}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GradeCriteriaGuideDialog;
